import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Lock, AlertTriangle, CheckCircle2, KeyRound, RefreshCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { log } from '../../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * iter114s.24 — Admin self-service secret rotation.
 *
 * Lists every secret tracked by the Mongo-backed bootstrap from
 * iter114s.23 (`utils/secrets_store.py:PERSISTENT_KEYS`) along with
 * its presence + length in the current backend process AND in Mongo
 * (the durable copy that survives pod rebuilds). Lets an admin upsert
 * a new value without ever needing to ask the agent to rewrite
 * `/app/backend/.env`.
 *
 * Security
 * --------
 *  * Endpoint is admin-gated (`Depends(get_current_admin_user)`).
 *  * Backend NEVER returns the actual value — only presence + length.
 *    A screenshot of this panel is leak-safe.
 *  * The "Rotate" inline form is a one-shot input (cleared on submit)
 *    that POSTs to `/api/admin/secrets/{key}`. The backend persists
 *    to Mongo + writes into `os.environ` so the change is live for
 *    the next request, not just the next rebuild.
 *
 * Length is the only metadata we surface because it lets the admin
 * spot a truncated paste (e.g. CONVERGE_PIN saved as 20 chars when
 * Elavon issues 64-char PINs) without seeing the secret itself.
 */
export const IntegrationSecretsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // key currently being rotated
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchSecrets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/secrets`, { withCredentials: true });
      setRows(res.data?.secrets || []);
    } catch (e) {
      log.warn('[secrets] list failed', e?.response?.status || e?.message);
      toast.error('Failed to load integration secrets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSecrets();
  }, []);

  const submit = async () => {
    if (!editing || !draft.trim()) return;
    try {
      setSaving(true);
      await axios.post(
        `${API}/admin/secrets/${editing}`,
        { value: draft },
        { withCredentials: true }
      );
      toast.success(`${editing} rotated. Live for the next request.`);
      setDraft('');
      setEditing(null);
      await fetchSecrets();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'unknown error';
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const startRotate = (key) => {
    setEditing(key);
    setDraft('');
  };

  const cancel = () => {
    setEditing(null);
    setDraft('');
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6"
      data-testid="integration-secrets-panel"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-bold text-[#2F5D3A] flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Integration Secrets
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Rotate Freightcom / Converge / TOTP keys without an agent. Values are
            persisted to MongoDB (durable across pod rebuilds) and live in{' '}
            <code className="bg-gray-100 px-1 rounded">os.environ</code> for the
            next request — no backend restart required.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchSecrets}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A7C59] hover:text-[#2F5D3A] px-3 py-1.5 rounded hover:bg-[#4A7C59]/10 disabled:opacity-50"
          data-testid="integration-secrets-refresh"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 italic">Loading…</div>
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2">Key</th>
                <th className="px-3 py-2">Live now?</th>
                <th className="px-3 py-2">Survives rebuild?</th>
                <th className="px-3 py-2">Length</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const live = r.live;
                const durable = r.durable;
                const lengthMismatch =
                  live && durable && r.env_length !== r.mongo_length;
                return (
                  <React.Fragment key={r.key}>
                    <tr
                      className="border-t border-gray-200"
                      data-testid={`secret-row-${r.key.toLowerCase()}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs">{r.key}</td>
                      <td className="px-3 py-2">
                        {live ? (
                          <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold">
                            <AlertTriangle className="w-3.5 h-3.5" /> No
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {durable ? (
                          <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold">
                            <Lock className="w-3.5 h-3.5" /> Yes
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold"
                            title="No Mongo copy — rotate this secret via the button below so it survives the next pod rebuild and propagates to every replica."
                          >
                            <AlertTriangle className="w-3.5 h-3.5" /> No — not saved
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {live ? `${r.env_length} chars` : '—'}
                        {lengthMismatch && (
                          <span
                            className="ml-2 text-amber-700 text-xs"
                            title={`Mongo holds ${r.mongo_length} chars but os.environ has ${r.env_length}. Likely a mid-rotation race; refresh.`}
                          >
                            ⚠ mismatch
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {editing === r.key ? null : (
                          <button
                            type="button"
                            onClick={() => startRotate(r.key)}
                            className="text-xs font-medium text-[#4A7C59] hover:text-[#2F5D3A] underline-offset-2 hover:underline"
                            data-testid={`secret-rotate-${r.key.toLowerCase()}`}
                          >
                            Rotate
                          </button>
                        )}
                      </td>
                    </tr>
                    {editing === r.key && (
                      <tr className="bg-amber-50 border-t border-amber-200">
                        <td colSpan={5} className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="password"
                              value={draft}
                              onChange={(e) => setDraft(e.target.value)}
                              placeholder={`New value for ${r.key}`}
                              className="flex-1 px-3 py-1.5 border border-gray-300 rounded font-mono text-xs"
                              autoFocus
                              data-testid={`secret-input-${r.key.toLowerCase()}`}
                            />
                            <button
                              type="button"
                              onClick={submit}
                              disabled={saving || !draft.trim()}
                              className="inline-flex items-center gap-1 bg-[#4A7C59] hover:bg-[#3a6346] disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded"
                              data-testid={`secret-save-${r.key.toLowerCase()}`}
                            >
                              <Save className="w-3.5 h-3.5" /> Save
                            </button>
                            <button
                              type="button"
                              onClick={cancel}
                              disabled={saving}
                              className="text-gray-600 hover:text-gray-900 text-xs px-2 py-1.5"
                            >
                              Cancel
                            </button>
                          </div>
                          <p className="text-xs text-amber-800 italic mt-1">
                            Saving persists to MongoDB AND updates the live process env.
                            Effective on the next request, no backend restart needed.
                          </p>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default IntegrationSecretsPanel;
